// client/src/utils/adminUtils.js
import React from "react";
import PropTypes from "prop-types";
import {
  USER_TYPES,
  EMAIL_VALIDATION_REGEX,
} from "../constants/adminConstants";
import { CURRENT_MEETING } from "../constants.js";
import Button from "components/CustomButtons/Button.js";
import Edit from "@material-ui/icons/Edit";
import Info from "@material-ui/icons/Info";

// Utility Functions
export const validateEmail = (email) => EMAIL_VALIDATION_REGEX.test(email);

export const getUserTypeCode = (typeNumeric) => {
  const userType = Object.values(USER_TYPES).find(
    (type) => type.value === typeNumeric
  );
  return userType ? userType.code : "member";
};

export const filterUsersByType = (users, type) => {
  return users.filter((user) => user.type === type);
};

export const getEmailList = (users) => {
  return users.map((user) => user.email).join("; ");
};

// Edit Button Component
export const EditButton = ({ onClick }) => (
  <div className="actions-right">
    <Button
      justIcon
      round
      simple
      onClick={onClick}
      color="warning"
      className="edit"
    >
      <Edit />
    </Button>
  </div>
);

EditButton.propTypes = {
  onClick: PropTypes.func.isRequired,
};

// Data Processing Functions
export const processVoteData = (attendance) => {
  const votes = attendance.filter((item) => item.type.indexOf("vote") > -1);

  const mostRecentVotes = Object.values(
    votes.reduce((acc, vote) => {
      if (!acc[vote.email] || acc[vote.email].dateTime < vote.dateTime) {
        acc[vote.email] = vote;
      }
      return acc;
    }, {})
  );

  return {
    emeriaud: mostRecentVotes.filter(
      (vote) => vote.type.indexOf("emeriaud") > -1
    ).length,
    abusultaneh: mostRecentVotes.filter(
      (vote) => vote.type.indexOf("abusultaneh") > -1
    ).length,
    grunwell: mostRecentVotes.filter(
      (vote) => vote.type.indexOf("grunwell") > -1
    ).length,
  };
};

export const processRegistrationData = (
  registrations,
  statNumeric,
  openRegistrationModal
) => {
  const statTypeText = statNumeric === "2" ? CURRENT_MEETING : "";

  const selectedRegistrations = registrations.filter(
    (reg) => reg.conference_name === statTypeText
  );

  return {
    total: selectedRegistrations.length,
    inPerson: selectedRegistrations.filter(
      (reg) => reg.registration_type === "in_person"
    ).length,
    virtual: selectedRegistrations.filter(
      (reg) => reg.registration_type === "virtual"
    ).length,
    data: selectedRegistrations.map((prop) => ({
      id: prop.id,
      name: prop.name,
      institution: prop.institution,
      role: prop.role,
      registration_type: prop.registration_type,
      need_time: prop.need_time ? "Yes" : "No",
      actions: (
        <div className="actions-right">
          <Button
            justIcon
            round
            simple
            onClick={() => openRegistrationModal(prop)}
            color="info"
            className="info"
          >
            <Info />
          </Button>
        </div>
      ),
    })),
  };
};

export const processUserData = (
  users,
  openModal,
  updateFormData,
  confirmDeleteUser
) => {
  return users.map((prop) => ({
    id: prop.id,
    name: prop.name,
    email: prop.email,
    type: prop.type,
    institution: prop.institution,
    actions: (
      <div className="actions-right">
        <Button
          justIcon
          round
          simple
          onClick={() => {
            updateFormData("id", prop.id);
            updateFormData("name", prop.name);
            updateFormData("email", prop.email);
            updateFormData("institution", prop.institution);
            updateFormData("about", prop.about);
            updateFormData(
              "typeNumeric",
              prop.type === "member_admin"
                ? "3"
                : prop.type === "admin"
                ? "4"
                : prop.type === "research_coordinator"
                ? "5"
                : "2"
            );
            openModal("updateUser");
          }}
          color="warning"
          className="edit"
        >
          <Edit />
        </Button>
        <Button
          justIcon
          round
          simple
          onClick={() => confirmDeleteUser(prop.id, prop.name)}
          color="danger"
          className="delete"
        >
          ✗
        </Button>
      </div>
    ),
  }));
};

export const processAttendanceData = (attendance) => {
  // Format date function
  const formatDateTime = (dateTimeString) => {
    try {
      const date = new Date(dateTimeString);
      // Format as MM-DD-YYYY HH:MM am/pm
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'

      return `${month}-${day}-${year} ${hours}:${minutes} ${ampm}`;
    } catch (error) {
      // If date parsing fails, return original string
      return dateTimeString;
    }
  };

  // Generate attendance summary by meeting type
  const attendanceSummary = {};

  attendance.forEach((record) => {
    const type = record.type;
    // Extract meeting name from type (this might need adjustment based on your data structure)
    let meetingName = type;

    // Try to format meeting names based on common patterns
    if (type.includes("prevent_meet")) {
      if (type.includes("spring_2024")) {
        meetingName = "PREVENT Spring 2024 Off Cycle Meeting Attendance";
      } else if (type.includes("fall_2024")) {
        meetingName = "PREVENT Fall 2024 Meeting Attendance";
      } else if (type.includes("winter_2024")) {
        meetingName = "PREVENT Winter 2024 Meeting Attendance";
      } else if (type.includes("spring_2025")) {
        meetingName = "PREVENT Spring 2025 Meeting Attendance";
      } else {
        meetingName = `${type} Attendance`;
      }
    }

    if (!attendanceSummary[meetingName]) {
      attendanceSummary[meetingName] = 0;
    }
    attendanceSummary[meetingName]++;
  });

  const processedData = attendance.map((prop) => ({
    id: prop.id,
    name: prop.name,
    email: prop.email,
    type: prop.type,
    dateTime: formatDateTime(prop.dateTime),
  }));

  return {
    data: processedData,
    summary: attendanceSummary,
  };
};

export const processDataRequestsData = (
  dataRequests,
  updateFormData,
  openModal
) => {
  return dataRequests.map((prop) => ({
    id: prop.id,
    name: prop.name,
    actions: (
      <div className="actions-right">
        <Button
          justIcon
          round
          simple
          onClick={() => {
            updateFormData("id", prop.id);
            updateFormData("name", prop.name);
            updateFormData("email", prop.email);
            updateFormData("institution", prop.institution);
            updateFormData("type", prop.type);
            updateFormData("description", prop.description);
            openModal("dataRequestsInfo");
          }}
          color="info"
          className="info"
        >
          <Info />
        </Button>
      </div>
    ),
  }));
};

export const processTentativeData = (
  tentatives,
  confirmApproveUser,
  confirmDenyUser
) => {
  return tentatives.map((prop) => ({
    id: prop.id,
    name: prop.name,
    institution: prop.institution,
    message: prop.message,
    email: prop.email,
    actions: (
      <div className="actions-right">
        <Button
          justIcon
          round
          simple
          onClick={() =>
            confirmApproveUser(
              prop.id,
              prop.email,
              prop.name,
              "member",
              prop.institution,
              ""
            )
          }
          color="success"
          className="approve"
        >
          ✓
        </Button>
        <Button
          justIcon
          round
          simple
          onClick={() => confirmDenyUser(prop.id, prop.name)}
          color="danger"
          className="deny"
        >
          ✗
        </Button>
      </div>
    ),
  }));
};
